import { useState } from 'react';

import { useGetStudentById } from '~entities/student';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { FamilyHubLinkButton } from '~features/family-hub/ui/family-hub-link-button';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

import { StudentDetails } from './student-details';
import { UpdateStudentForm } from './update-student-from';

export interface StudentDetailPageProps {
  studentId: string;
  /** Open the edit form immediately (family hub pencil → details?edit=true). */
  initialEditing?: boolean;
}

export const StudentDetailPage: React.FC<StudentDetailPageProps> = ({
  studentId,
  initialEditing = false,
}) => {
  const [isEditing, setIsEditing] = useState(initialEditing);
  const { data, isPending } = useGetStudentById(studentId);
  const { hasAdminAccess, isRolesReady } = useCurrentUserRole();
  const canViewFamily = isRolesReady && hasAdminAccess;

  // ENR-2522: parent route already shells the page; only gate when no data yet.
  if (isPending && !data) {
    return null;
  }

  if (!data) {
    return <div>Student not found</div>;
  }

  const familyButtonLabel = data.familyName?.trim()
    ? `View ${data.familyName.trim()} family`
    : 'View family';

  return (
    <Card className='mb-10 w-full max-w-3xl '>
      <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-3 space-y-0'>
        <CardTitle>Basic Info</CardTitle>
        {canViewFamily ? (
          <FamilyHubLinkButton
            studentId={studentId}
            parentId={data.parentId}
            label={familyButtonLabel}
            className='hidden md:inline-flex'
          />
        ) : null}
      </CardHeader>
      <CardContent className='mt-5 space-y-8'>
        {isEditing ? (
          <UpdateStudentForm
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            data={data}
          />
        ) : (
          <StudentDetails
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            data={data}
          />
        )}
      </CardContent>
    </Card>
  );
};
